import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Star, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { api, type GraphicElement } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export default function Elementy() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState("approved");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState<GraphicElement | null>(null);
  const metaQ = useQuery({ queryKey: ["elements-meta"], queryFn: () => api.elementsMeta() });
  const listQ = useQuery({
    queryKey: ["elements", status, kind, q],
    queryFn: () => api.elementsList({ status, kind, q, limit: 120 }),
  });
  const seedM = useMutation({
    mutationFn: () => api.elementsSeed(false),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["elements"] }),
  });
  const genM = useMutation({
    mutationFn: () =>
      api.elementsGenerate({
        kind: kind || "arrow",
        count: 3,
        engine: "procedural",
        status: "pending",
        brief: "zamówienie z /elementy",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["elements"] }),
  });
  const uploadM = useMutation({
    mutationFn: (file: File) => api.elementsUpload(file, kind || "icon"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["elements"] }),
  });
  const patchStatusM = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "retire" | "star" }) => {
      if (action === "approve") return api.elementsApprove(id);
      if (action === "retire") return api.elementsRetire(id, "ręcznie");
      return api.elementsStar(id, !preview?.starred);
    },
    onSuccess: (el) => {
      setPreview(el);
      qc.invalidateQueries({ queryKey: ["elements"] });
    },
  });

  const items = listQ.data?.items || [];
  const kinds = metaQ.data?.taxonomy?.kinds || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Elementy"
        subtitle="Biblioteka grafik Warsztatu 1-grafika (jedna ręka, tagi, kurator)."
      />
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="approved">approved</option>
          <option value="pending">pending</option>
          <option value="retired">retired</option>
          <option value="">wszystkie</option>
        </select>
        <select
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="">kind…</option>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <Input className="max-w-xs" value={q} onChange={(e) => setQ(e.target.value)} placeholder="szukaj" />
        <Button type="button" variant="outline" disabled={seedM.isPending} onClick={() => seedM.mutate()}>
          {seedM.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Załaduj seed
        </Button>
        <Button type="button" disabled={genM.isPending} onClick={() => genM.mutate()}>
          {genM.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Zamów 3
        </Button>
        <Button type="button" variant="secondary" onClick={() => fileRef.current?.click()}>
          <Upload className="mr-2 h-4 w-4" />
          Upload
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadM.mutate(f);
            e.target.value = "";
          }}
        />
        <Button type="button" variant="ghost" asChild>
          <Link to="/karuzele">Wróć do kreatora</Link>
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-6">
        {items.map((el) => (
          <Card
            key={el.id}
            className={cn("cursor-pointer overflow-hidden", preview?.id === el.id && "ring-2 ring-brand")}
            onClick={() => setPreview(el)}
          >
            <CardContent className="space-y-1 bg-[#f8f5ef] p-2">
              <img src={api.elementsPng(el.id, true)} alt={el.short || el.id} className="aspect-square w-full object-contain" />
              <div className="truncate text-[11px] font-medium">{el.kind}</div>
              <div className="truncate text-[10px] text-muted-foreground">{el.tag_ids?.slice(0, 3).join(", ")}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      {preview ? (
        <Card>
          <CardContent className="flex flex-wrap items-start gap-4 p-4">
            <img
              src={api.elementsPng(preview.id, false)}
              alt={preview.short || preview.id}
              className="h-48 w-48 rounded-md bg-[#f8f5ef] object-contain"
            />
            <div className="min-w-0 flex-1 space-y-2 text-sm">
              <div className="font-semibold">{preview.short || preview.id}</div>
              <div className="text-muted-foreground">
                {preview.kind} · {preview.family_id} · {preview.status} · {preview.source}
              </div>
              <div>Tagi: {(preview.tag_ids || []).join(", ") || "-"}</div>
              <div className="flex flex-wrap gap-2">
                {preview.status !== "approved" ? (
                  <Button
                    type="button"
                    size="sm"
                    disabled={patchStatusM.isPending}
                    onClick={() => patchStatusM.mutate({ id: preview.id, action: "approve" })}
                  >
                    Approve
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={patchStatusM.isPending}
                  onClick={() => patchStatusM.mutate({ id: preview.id, action: "star" })}
                >
                  <Star className={cn("mr-1 h-3.5 w-3.5", preview.starred && "fill-current")} />
                  Gwiazdka
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  disabled={patchStatusM.isPending}
                  onClick={() => patchStatusM.mutate({ id: preview.id, action: "retire" })}
                >
                  Retire
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
